import { useEffect, useMemo, useRef, useState } from "react";
import { GlBasemap } from "../lib/basemap";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Eye,
  MapPin,
  NavigationArrow,
  Image as ImageIcon,
  DeviceMobile,
  Monitor,
  DeviceTablet,
  ArrowsClockwise,
  GlobeHemisphereWest,
  LockKey,
  Sparkle,
  ArrowsOut,
  X,
  Crosshair,
  CaretDown,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const EVENT_META = {
  profile_view:    { label: "Viewed profile",     icon: Eye,             tone: "text-primary" },
  pin_click:       { label: "Clicked your pin",   icon: MapPin,          tone: "text-amber-500" },
  menu_view:       { label: "Opened menu",        icon: ImageIcon,       tone: "text-emerald-500" },
  directions_click:{ label: "Tapped Directions",  icon: NavigationArrow, tone: "text-sky-500" },
};

const DEVICE_ICON = {
  mobile: DeviceMobile,
  tablet: DeviceTablet,
  desktop: Monitor,
};

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 45) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}

function locationLabel(row) {
  if (row.city && row.region) return `${row.city}, ${row.region}`;
  if (row.city) return row.city;
  if (row.region) return row.region;
  return "Unknown location";
}

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 days" },
  { key: "30d",   label: "30 days" },
];

export default function ViewersCard() {
  const { user } = useAuth();
  const isPro = !!user?.is_pro || !!user?.is_admin;

  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userPos, setUserPos] = useState(null); // [lat, lng] — browser geo for initial center
  const [expanded, setExpanded] = useState(false); // fullscreen heatmap modal
  // Collapsed by default — the heatmap above is the primary signal;
  // the per-event row feed is supporting detail.
  const [recentOpen, setRecentOpen] = useState(false);

  // Ask for browser geolocation once on mount so the heatmap can open
  // centered on the owner's actual area (same UX as the customer map).
  // Failure / denial just leaves userPos null and we fall back to the
  // data centroid → continental-US view.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos([pos.coords.latitude, pos.coords.longitude]);
      },
      () => { /* denied or failed — fine, fallback handles it */ },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  }, []);

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get(`/analytics/viewers?range=${range}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load viewer activity");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  const recent = data?.recent || [];
  const summary = data?.summary || { total: 0, by_event_type: {}, by_device: {} };
  const heatmap = data?.heatmap || [];
  const topCities = data?.top_cities || [];
  const topRef = data?.top_referrers || [];

  // Initial-center priority:
  //   1) Centroid of heatmap dots (so the data is always visible if present)
  //   2) Owner's browser geolocation (so an empty map still feels "their area")
  //   3) Continental-US fallback
  const heatCenter = useMemo(() => {
    if (heatmap.length) {
      const sum = heatmap.reduce((acc, p) => [acc[0] + p.lat, acc[1] + p.lng], [0, 0]);
      return [sum[0] / heatmap.length, sum[1] / heatmap.length];
    }
    if (userPos) return userPos;
    return [39.5, -98.35];
  }, [heatmap, userPos]);
  const heatZoom = heatmap.length > 1 ? 8 : (heatmap.length === 1 ? 10 : (userPos ? 10 : 4));

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="owner-viewers-card"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <GlobeHemisphereWest weight="fill" className="h-3.5 w-3.5" />
            Who&apos;s looking
          </div>
          <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">
            Where &amp; when people view you
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Anonymous viewer activity — your own visits are excluded.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-full border border-border bg-background p-0.5"
            data-testid="viewers-range-toggle"
          >
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={
                  "h-8 px-3 rounded-full text-xs font-medium transition " +
                  (range === r.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground")
                }
                data-testid={`viewers-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
            aria-label="Refresh viewer activity"
            data-testid="viewers-refresh-btn"
          >
            <ArrowsClockwise
              weight="duotone"
              className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")}
            />
          </button>
        </div>
      </header>

      {/* Summary tiles */}
      <div className="grid gap-2.5 mt-5 grid-cols-2 md:grid-cols-4">
        <SummaryTile
          label="Total views"
          value={loading ? null : summary.total}
          icon={Eye}
          testid="viewers-total"
          accent
        />
        {["profile_view", "pin_click", "menu_view", "directions_click"].slice(0, 3).map((k) => (
          <SummaryTile
            key={k}
            label={EVENT_META[k].label}
            value={loading ? null : (summary.by_event_type?.[k] || 0)}
            icon={EVENT_META[k].icon}
            testid={`viewers-count-${k}`}
          />
        ))}
      </div>

      {/* Device split chip row */}
      {!loading && summary.total > 0 && (
        <DeviceBar by={summary.by_device || {}} />
      )}

      {/* Heatmap (Pro) / locked teaser (Free) */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
            Viewer heatmap
          </h3>
          <div className="flex items-center gap-2">
            {isPro && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 transition"
                data-testid="viewers-heatmap-expand-btn"
                aria-label="Expand heatmap"
              >
                <ArrowsOut weight="bold" className="h-3.5 w-3.5" />
                Expand
              </button>
            )}
            {!isPro && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
                <LockKey weight="fill" className="h-3 w-3" />
                Pro
              </span>
            )}
          </div>
        </div>

        <div
          className="relative rounded-xl border border-border overflow-hidden"
          data-testid="viewers-heatmap-wrap"
        >
          <HeatmapMap
            center={heatCenter}
            zoom={heatZoom}
            points={isPro ? heatmap : []}
            userPos={userPos}
            height={280}
            interactive
            onClickEmpty={isPro ? () => setExpanded(true) : undefined}
          />

          {/* Locked overlay for non-Pro */}
          {!isPro && (
            <div
              className="absolute inset-0 grid place-items-center bg-background/65 backdrop-blur-[3px]"
              data-testid="viewers-heatmap-locked"
            >
              <div className="text-center px-6 max-w-sm">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">
                  <Sparkle weight="fill" className="h-3 w-3" />
                  Pro feature
                </div>
                <h4 className="font-display font-bold text-lg mt-1">
                  See where your viewers are
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Upgrade to Pro to unlock a privacy-jittered map of where
                  people are when they look up your truck — plus top cities
                  and traffic sources.
                </p>
              </div>
            </div>
          )}

          {isPro && !loading && heatmap.length === 0 && (
            <div className="absolute inset-x-0 top-3 mx-auto w-fit max-w-[92%] rounded-full bg-background/90 backdrop-blur-sm border border-border px-3 py-1.5 shadow-sm pointer-events-none">
              <p className="text-[11px] text-muted-foreground text-center">
                No viewer locations yet — dots appear as new visitors view your truck.
              </p>
            </div>
          )}
        </div>
        <p className="mt-1.5 text-[10px] text-muted-foreground leading-tight">
          Dots are intentionally jittered up to ~1 mile so individual viewers stay anonymous.
          {isPro && " Click Expand for a larger view."}
        </p>
      </div>

      {/* Fullscreen heatmap modal — Pro only */}
      {expanded && isPro && (
        <FullscreenHeatmap
          center={heatCenter}
          zoom={Math.max(heatZoom, 9)}
          points={heatmap}
          userPos={userPos}
          onClose={() => setExpanded(false)}
        />
      )}

      {/* Recent activity feed — collapsed by default; click the heading
          to expand. The heatmap above carries the primary signal. */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setRecentOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition mb-2"
          aria-expanded={recentOpen}
          data-testid="viewers-recent-toggle"
        >
          <CaretDown
            weight="bold"
            className={`h-3 w-3 transition-transform ${recentOpen ? "rotate-180" : ""}`}
          />
          Recent activity
          {!recentOpen && recent.length > 0 && (
            <span className="text-[10px] normal-case tabular-nums font-semibold text-muted-foreground/80">
              · {recent.length}
            </span>
          )}
        </button>
        {recentOpen && (
        <div className="rounded-xl border border-border divide-y divide-border" data-testid="viewers-recent-feed">
          {loading ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No viewer activity for this window yet.
            </div>
          ) : (
            recent.map((row, i) => {
              const meta = EVENT_META[row.event_type] || { label: row.event_type, icon: Eye, tone: "text-muted-foreground" };
              const DIcon = DEVICE_ICON[row.device] || Monitor;
              return (
                <div
                  key={i}
                  className="px-3 py-2.5 flex items-center gap-3"
                  data-testid="viewers-recent-row"
                >
                  <div className={`h-8 w-8 grid place-items-center rounded-full bg-background border border-border flex-none ${meta.tone}`}>
                    <meta.icon weight="duotone" className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {meta.label}
                      <span className="text-muted-foreground font-normal"> · {row.truck_name}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                      <DIcon weight="duotone" className="h-3 w-3" />
                      <span className="capitalize">{row.device}</span>
                      <span>·</span>
                      <span>{locationLabel(row)}</span>
                      {row.referrer && (
                        <>
                          <span>·</span>
                          <span className="text-primary/80">from {row.referrer}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums flex-none">
                    {timeAgo(row.created_at)}
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}
      </div>

      {/* Top cities + referrers — Pro only */}
      {isPro && !loading && (topCities.length > 0 || topRef.length > 0) && (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {topCities.length > 0 && (
            <div className="rounded-xl border border-border p-3" data-testid="viewers-top-cities">
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Top cities
              </h4>
              <ul className="space-y-1.5">
                {topCities.map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate">
                      {c.city}
                      {c.region && <span className="text-muted-foreground">, {c.region}</span>}
                    </span>
                    <span className="tabular-nums font-medium">{c.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {topRef.length > 0 && (
            <div className="rounded-xl border border-border p-3" data-testid="viewers-top-referrers">
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Traffic sources
              </h4>
              <ul className="space-y-1.5">
                {topRef.map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate capitalize">{r.source}</span>
                    <span className="tabular-nums font-medium">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SummaryTile({ label, value, icon: Icon, testid, accent = false }) {
  return (
    <div
      className={
        "rounded-xl border p-3 flex flex-col " +
        (accent
          ? "border-primary/30 bg-primary/5"
          : "border-border bg-background/50")
      }
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <Icon weight="duotone" className={"h-4 w-4 " + (accent ? "text-primary" : "text-muted-foreground")} />
      </div>
      <div className="font-display font-bold text-3xl tabular-nums leading-none mt-2">
        {value === null ? "…" : value}
      </div>
    </div>
  );
}

function DeviceBar({ by }) {
  const total = (by.mobile || 0) + (by.tablet || 0) + (by.desktop || 0) + (by.unknown || 0);
  if (total === 0) return null;
  const pct = (n) => Math.round(((n || 0) / total) * 100);
  return (
    <div className="mt-3 flex items-center gap-3 text-[11px]" data-testid="viewers-device-bar">
      <span className="uppercase tracking-wider text-muted-foreground">Devices</span>
      <div className="flex-1 h-2 rounded-full overflow-hidden bg-muted flex">
        <div className="bg-primary" style={{ width: `${pct(by.mobile)}%` }} title={`Mobile ${pct(by.mobile)}%`} />
        <div className="bg-emerald-500" style={{ width: `${pct(by.tablet)}%` }} title={`Tablet ${pct(by.tablet)}%`} />
        <div className="bg-sky-500" style={{ width: `${pct(by.desktop)}%` }} title={`Desktop ${pct(by.desktop)}%`} />
      </div>
      <span className="text-muted-foreground tabular-nums">
        {pct(by.mobile)}% mobile · {pct(by.desktop)}% desktop
      </span>
    </div>
  );
}


/**
 * Shared heatmap renderer used both inline (compact 280px) and in the
 * fullscreen modal. Enables zoom controls + scroll wheel so the user can
 * zoom IN and OUT (the original inline-only version disabled both).
 */
function HeatmapMap({ center, zoom, points, userPos, height = 280, interactive = true, onClickEmpty }) {
  return (
    <div
      style={{ height }}
      onClick={(e) => {
        if (onClickEmpty && (!points || points.length === 0)) {
          e.preventDefault();
          onClickEmpty();
        }
      }}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={interactive}
        zoomControl={interactive}
        attributionControl={false}
        data-testid="viewers-heatmap"
      >
        <GlBasemap />
        <RecenterOnChange center={center} zoom={zoom} />
        {userPos && (
          <CircleMarker
            center={userPos}
            radius={5}
            pathOptions={{
              color: "#0ea5e9",
              weight: 2,
              fillColor: "#0ea5e9",
              fillOpacity: 0.85,
            }}
          >
            <Tooltip direction="top" offset={[0, -4]} opacity={0.9}>
              <span className="text-[11px]">You are here</span>
            </Tooltip>
          </CircleMarker>
        )}
        {(points || []).map((p, i) => (
          <CircleMarker
            key={i}
            center={[p.lat, p.lng]}
            radius={8}
            pathOptions={{
              color: "#ea580c",
              weight: 1,
              fillColor: "#ea580c",
              fillOpacity: 0.5,
            }}
          >
            <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
              <div className="text-[11px]">
                <div className="font-semibold capitalize">
                  {(EVENT_META[p.event_type]?.label) || p.event_type}
                </div>
                <div className="text-muted-foreground">{timeAgo(p.when)}</div>
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

/**
 * Imperative bridge so the map view recenters when center/zoom change
 * after mount (e.g. geolocation resolves after the data loads, or the
 * data refreshes with a new centroid). MapContainer alone only honors
 * center/zoom on first mount.
 */
function RecenterOnChange({ center, zoom }) {
  const map = useMap();
  const lastRef = useRef("");
  useEffect(() => {
    const key = `${center?.[0]},${center?.[1]},${zoom}`;
    if (key === lastRef.current) return;
    lastRef.current = key;
    try {
      map.setView(center, zoom, { animate: true });
    } catch {
      // ignore — leaflet sometimes throws if map isn't ready
    }
  }, [center, zoom, map]);
  return null;
}

/**
 * Fullscreen heatmap modal. Same HeatmapMap renderer at large size with
 * a backdrop + close button. Escape and backdrop click both dismiss.
 */
function FullscreenHeatmap({ center, zoom, points, userPos, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[10000] bg-background/80 backdrop-blur-sm grid place-items-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="viewers-heatmap-fullscreen"
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Crosshair weight="duotone" className="h-4 w-4 text-primary flex-none" />
            <h3 className="font-display font-bold text-base sm:text-lg truncate">
              Viewer heatmap · expanded
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Close heatmap"
            data-testid="viewers-heatmap-fullscreen-close"
          >
            <X weight="bold" className="h-5 w-5" />
          </button>
        </header>
        <HeatmapMap
          center={center}
          zoom={zoom}
          points={points}
          userPos={userPos}
          height="min(75vh, 720px)"
          interactive
        />
        <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border">
          Pinch / scroll to zoom · drag to pan · dots are jittered up to ~1 mile for privacy.
        </p>
      </div>
    </div>
  );
}
